if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupProductGalleryVideoControls();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.querySelectorAll('.js-product-gallery-youtube').forEach((video) => {
          video.addEventListener('load', () => {
            const mediaItem = video.closest('[data-media-id]');
            this.setProductGalleryVideoState(mediaItem?.classList.contains('is-active') ? mediaItem : null);
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.playActiveMedia(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        this.setProductGalleryVideoState(activeItem);
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setProductGalleryVideoState(activeItem) {
        this.querySelectorAll('.js-product-gallery-native').forEach((video) => {
          const shouldPlay = activeItem?.contains(video);
          if (!shouldPlay) {
            video.pause();
            return;
          }

          // Muted inline playback is the most reliable path on mobile Safari.
          video.muted = true;
          const playPromise = video.play();
          playPromise?.catch(() => {
            const player = video.closest('.product-gallery-video');
            if (player) player.dataset.playerState = 'paused';
          });
        });

        this.querySelectorAll('.js-product-gallery-youtube').forEach((video) => {
          const shouldPlay = activeItem?.contains(video);
          const youtubePlayer = this.productGalleryVideoPlayers?.get(video);
          if (youtubePlayer) {
            if (shouldPlay) {
              youtubePlayer.playVideo();
            } else {
              youtubePlayer.pauseVideo();
            }
            return;
          }
          video.contentWindow?.postMessage(
            JSON.stringify({
              event: 'command',
              func: shouldPlay ? 'playVideo' : 'pauseVideo',
              args: [],
            }),
            '*'
          );
        });
      }

      setupProductGalleryVideoControls() {
        this.productGalleryVideoPlayers = new Map();

        this.querySelectorAll('.js-product-gallery-native').forEach((video) => {
          const player = video.closest('.product-gallery-video');
          if (!player) return;

          const seekControl = player.querySelector('.product-gallery-video__seek');
          let isSeeking = false;
          let seekSyncTimer;
          let pendingSeekTime = null;
          const formatTime = (value) => {
            if (!Number.isFinite(value)) return '0:00';
            const minutes = Math.floor(value / 60);
            const seconds = Math.floor(value % 60)
              .toString()
              .padStart(2, '0');
            return `${minutes}:${seconds}`;
          };
          const updateSeekControl = () => {
            if (isSeeking || !seekControl || !Number.isFinite(video.duration) || video.duration <= 0) return;
            const progress = Math.min(1, Math.max(0, video.currentTime / video.duration));
            seekControl.value = Math.round(progress * 1000);
            seekControl.style.setProperty('--video-seek-progress', `${progress * 100}%`);
            seekControl.setAttribute(
              'aria-valuetext',
              `${formatTime(video.currentTime)} of ${formatTime(video.duration)}`
            );
          };

          if (seekControl) {
            const finishSeeking = () => {
              window.clearTimeout(seekSyncTimer);
              if (pendingSeekTime !== null && Math.abs(video.currentTime - pendingSeekTime) > 0.5) {
                video.currentTime = pendingSeekTime;
                scheduleSeekSync();
                return;
              }
              pendingSeekTime = null;
              isSeeking = false;
              updateSeekControl();
            };
            const scheduleSeekSync = () => {
              window.clearTimeout(seekSyncTimer);
              seekSyncTimer = window.setTimeout(finishSeeking, 500);
            };
            seekControl.addEventListener('input', () => {
              if (!Number.isFinite(video.duration) || video.duration <= 0) return;
              isSeeking = true;
              const progress = Number(seekControl.value) / 1000;
              const targetTime = progress * video.duration;
              pendingSeekTime = targetTime;
              video.currentTime = targetTime;
              seekControl.style.setProperty('--video-seek-progress', `${progress * 100}%`);
              seekControl.setAttribute('aria-valuetext', `${formatTime(targetTime)} of ${formatTime(video.duration)}`);
              scheduleSeekSync();
            });
            ['pointerdown', 'touchstart'].forEach((eventName) => {
              seekControl.addEventListener(eventName, (event) => {
                isSeeking = true;
                event.stopPropagation();
              });
            });
            ['pointerup', 'pointercancel', 'touchend', 'touchcancel', 'change'].forEach((eventName) => {
              seekControl.addEventListener(eventName, (event) => {
                event.stopPropagation();
                scheduleSeekSync();
              });
            });
            seekControl.addEventListener('click', (event) => event.stopPropagation());
            video.addEventListener('seeked', finishSeeking);
            video.addEventListener('loadedmetadata', updateSeekControl);
            video.addEventListener('durationchange', updateSeekControl);
            video.addEventListener('timeupdate', updateSeekControl);
            updateSeekControl();

            // Shopify's local theme proxy does not serve byte-range responses for
            // video assets. Use a Blob URL locally so the preview can seek just
            // like Shopify's production CDN; this branch never runs storefront-side.
            if (['127.0.0.1', 'localhost'].includes(window.location.hostname)) {
              const localVideoSource = video.currentSrc || video.querySelector('source')?.src;
              if (localVideoSource) {
                window
                  .fetch(localVideoSource)
                  .then((response) => {
                    if (!response.ok) throw new Error(`Video request failed: ${response.status}`);
                    return response.blob();
                  })
                  .then((blob) => {
                    if (!video.isConnected) return;
                    const resumeTime = pendingSeekTime ?? video.currentTime;
                    const shouldResume = !video.paused;
                    const localVideoUrl = window.URL.createObjectURL(blob);
                    video.addEventListener(
                      'loadedmetadata',
                      () => {
                        if (resumeTime > 0) video.currentTime = Math.min(resumeTime, video.duration);
                        if (shouldResume) video.play().catch(() => {});
                      },
                      { once: true }
                    );
                    video.src = localVideoUrl;
                    video.load();
                  })
                  .catch(() => {
                    // Keep the original source as a graceful local-preview fallback.
                  });
              }
            }
          }

          player.dataset.playerReady = 'true';
          player.dataset.playerState = video.paused ? 'paused' : 'playing';
          video.addEventListener('playing', () => {
            player.dataset.playerState = 'playing';
          });
          video.addEventListener('pause', () => {
            player.dataset.playerState = 'paused';
          });
          video.addEventListener('waiting', () => {
            player.dataset.playerState = 'loading';
          });
        });

        this.querySelectorAll('.product-gallery-video').forEach((player) => {
          const video = player.querySelector('.js-product-gallery-youtube');
          if (!video) return;

          player.dataset.playerState = 'loading';

          this.loadYouTubeIframeApi().then(() => {
            if (!this.isConnected) return;
            new window.YT.Player(video, {
              events: {
                onReady: (event) => {
                  const youtubePlayer = event.target;
                  this.productGalleryVideoPlayers.set(video, youtubePlayer);
                  youtubePlayer.mute();
                  player.dataset.playerReady = 'true';

                  const mediaItem = video.closest('[data-media-id]');
                  if (mediaItem?.classList.contains('is-active')) {
                    youtubePlayer.playVideo();
                  } else {
                    youtubePlayer.pauseVideo();
                  }

                },
                onStateChange: (event) => {
                  const mediaItem = video.closest('[data-media-id]');
                  const isActiveVideo = mediaItem?.classList.contains('is-active');
                  if (isActiveVideo && event.data === window.YT.PlayerState.ENDED) {
                    event.target.seekTo(0, true);
                    event.target.playVideo();
                    return;
                  }
                  this.setProductGalleryVideoPlayerState(player, event.data);
                },
              },
            });
          });
        });
      }

      loadYouTubeIframeApi() {
        if (window.YT?.Player) return Promise.resolve(window.YT);
        if (window.productGalleryYouTubeApiPromise) return window.productGalleryYouTubeApiPromise;

        window.productGalleryYouTubeApiPromise = new Promise((resolve, reject) => {
          const previousReadyHandler = window.onYouTubeIframeAPIReady;
          window.onYouTubeIframeAPIReady = () => {
            previousReadyHandler?.();
            resolve(window.YT);
          };

          const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
          if (existingScript) return;

          const script = document.createElement('script');
          script.src = 'https://www.youtube.com/iframe_api';
          script.async = true;
          script.addEventListener('error', reject, { once: true });
          document.head.appendChild(script);
        });

        return window.productGalleryYouTubeApiPromise;
      }

      setProductGalleryVideoPlayerState(player, state) {
        if (state === window.YT.PlayerState.PLAYING) {
          player.dataset.playerState = 'playing';
        } else if (state === window.YT.PlayerState.PAUSED) {
          player.dataset.playerState = 'paused';
        } else if (state === window.YT.PlayerState.ENDED) {
          player.dataset.playerState = 'ended';
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
